// Lead finder logic: plain English -> filter chips (AI), and "Add as leads" (company, contact, deal at Lead),
// each checked against the do-not-contact list and existing records first.
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, type DataProvider, type Identifier } from "ra-core";

import { mapSizeToCategory } from "../../companies/sizes";
import type { CrmDataProvider } from "../../providers/types";

import type { IdealCustomer } from "../../types";
import {
  EMPLOYEE_RANGES,
  LeadFiltersSchema,
  type Lead,
  type LeadEngine,
  type LeadFilters,
} from "../../../../../supabase/functions/_shared/leadFilters.ts";
import { UNTRUSTED_NOTE, untrusted } from "../ai";
import { sectorFor } from "../companyLookup";
import {
  isOpenEngine,
  wikidataSearch,
  type OpenEngine,
} from "../../../../../supabase/functions/_shared/openData.ts";

export {
  domainOf,
  EMPLOYEE_RANGES,
  ENGINE_KEY,
  LEAD_ENGINES,
  LeadFiltersSchema,
  type KeyEngine,
  type Lead,
  type LeadEngine,
  type LeadFilters,
} from "../../../../../supabase/functions/_shared/leadFilters.ts";

export const buildFiltersPrompt = (description: string) =>
  [
    "Turn this description of the customers a B2B seller wants into lead search filters.",
    'keywords: up to 5 short industry or business keywords (for example "restaurant chain", "logistics").',
    "job_titles: up to 5 job titles of the people to contact, if the description names any.",
    'locations: up to 3 places as "City, Country" or a country.',
    `employee_ranges: any of ${EMPLOYEE_RANGES.map((r) => `"${r}"`).join(", ")} that match the size described.`,
    'Answer with JSON only: {"keywords": [], "job_titles": [], "locations": [], "employee_ranges": []}',
    "",
    UNTRUSTED_NOTE,
    untrusted("description", description.slice(0, 2000)),
  ].join("\n");

/** The filter schema, tolerant of an AI that answers a size range not on the list: such values are dropped */
export const AiFiltersSchema = LeadFiltersSchema.extend({
  employee_ranges: LeadFiltersSchema.shape.employee_ranges.catch([]),
});

export type SearchAnswer = {
  status: string;
  run_id?: string;
  results?: Lead[];
  excluded?: number;
};

export const capMessage = (used: number, cap: number) =>
  `${Math.min(used, cap)} of ${cap} look-ups used today`;

type AddResult = {
  added: number;
  skipped: string[];
  /** the new deal of each added lead, by lead ref */
  deals: Record<string, Identifier>;
};

/**
 * Create the ticked leads in order: for each, the company (reused when its website domain or name already
 * exists), then the contact (reused when the same name already works there), then a deal at stage "lead".
 * A lead whose domain or email is on the do-not-contact list is skipped entirely.
 */
export const addLeads = async (
  leads: Lead[],
  dataProvider: DataProvider & {
    isSuppressed(value: string): Promise<boolean>;
  },
  {
    salesId,
    stage = "lead",
    fits = {},
    sectors = [],
  }: {
    salesId?: Identifier;
    stage?: string;
    /** the configured company sectors, to set the sector from the lead's industry */
    sectors?: { value: string; label?: string }[];
    /** fit scores given in the lead finder, by lead ref; saved on the new company */
    fits?: Record<string, Record<string, unknown>>;
  },
): Promise<AddResult> => {
  const result: AddResult = { added: 0, skipped: [], deals: {} };
  for (const lead of leads) {
    const { company, contact } = lead;
    if (
      (company.domain && (await dataProvider.isSuppressed(company.domain))) ||
      (contact?.email && (await dataProvider.isSuppressed(contact.email)))
    ) {
      result.skipped.push(company.name);
      continue;
    }
    const source = { lead_source: lead.source, lead_source_ref: lead.ref };

    const { data: sameCompany } = await dataProvider.getList("companies", {
      filter: company.domain
        ? { "website@ilike": `%${company.domain}%` }
        : { "name@ilike": company.name },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    });
    // a company already in the CRM with a deal is not added again (a nightly run finds it once more)
    if (sameCompany[0]) {
      const { total } = await dataProvider.getList("deals", {
        filter: { company_id: sameCompany[0].id },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
      });
      if (total) {
        result.skipped.push(company.name);
        continue;
      }
    }
    const companyId =
      sameCompany[0]?.id ??
      (
        await dataProvider.create("companies", {
          data: {
            name: company.name,
            website:
              company.website ??
              (company.domain ? `https://${company.domain}` : undefined),
            phone_number: company.phone,
            address: company.address,
            city: company.city,
            country: company.country,
            linkedin_url: company.linkedin_url,
            description: company.industry,
            sector: sectorFor(
              `${company.name} ${company.industry ?? ""}`,
              sectors,
            ),
            // the engine's headcount, or the agent's estimate, as a size band
            size: company.employees
              ? mapSizeToCategory(company.employees)
              : undefined,
            sales_id: salesId,
            ...source,
            ...fits[lead.ref],
          },
        })
      ).data.id;

    let contactIds: Identifier[] = [];
    if (contact) {
      const { data: sameContact } = await dataProvider.getList("contacts", {
        filter: contact.email
          ? { "email_fts@ilike": `%${contact.email}%` }
          : {
              company_id: companyId,
              "first_name@ilike": contact.first_name,
              ...(contact.last_name
                ? { "last_name@ilike": contact.last_name }
                : {}),
            },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
      });
      const now = new Date().toISOString();
      const contactId =
        sameContact[0]?.id ??
        (
          await dataProvider.create("contacts", {
            data: {
              first_name: contact.first_name,
              last_name: contact.last_name ?? "",
              title: contact.title,
              linkedin_url: contact.linkedin_url,
              company_id: companyId,
              email_jsonb: contact.email
                ? [{ email: contact.email, type: "Work" }]
                : [],
              phone_jsonb: [],
              tags: [],
              first_seen: now,
              last_seen: now,
              sales_id: salesId,
              ...source,
            },
          })
        ).data.id;
      contactIds = [contactId];
    }

    const { data: deal } = await dataProvider.create("deals", {
      data: {
        name: company.name,
        company_id: companyId,
        contact_ids: contactIds,
        stage,
        sales_id: salesId,
        index: 0,
        // the deal screens, column totals and dashboard read amount as a number; a lead has no value yet
        amount: 0,
      },
    });
    result.added++;
    result.deals[lead.ref] = deal.id;
  }
  return result;
};

export const DEFAULT_DAILY_CAP = 50;
/** The cap as saved: a whole number from 1 to 10 000, else the default */
export const dailyCap = (value: unknown) => {
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n >= 1 && n <= 10_000 ? n : DEFAULT_DAILY_CAP;
};

/** Which lead engines have a key (admin and sales; empty for delivery) */
export const useEngineStatus = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: ["lead_engines", "status"],
    queryFn: () => dataProvider.engineKeyStatus(),
  });
};

export const POLL_MS = 5_000;
export const MAX_POLLS = 60; // five minutes

/** One search page; a Google Maps run takes a minute or two, so it is polled until it has places */
export const runLeadSearch = async (
  dataProvider: Pick<CrmDataProvider, "leadSearch">,
  engine: LeadEngine | OpenEngine,
  filters: LeadFilters,
  page = 1,
  stillRunning = "The map search is still running",
): Promise<SearchAnswer> => {
  // free sources are asked from the browser; the keyed engines go through lead_search (keys, daily cap)
  if (isOpenEngine(engine))
    return {
      status: "SUCCEEDED",
      results: await wikidataSearch(LeadFiltersSchema.parse(filters)),
    } as SearchAnswer;
  let answer = await dataProvider.leadSearch<SearchAnswer>({
    action: "search",
    engine,
    filters: LeadFiltersSchema.parse(filters),
    page,
  });
  for (let i = 0; answer.run_id && !answer.results && i < MAX_POLLS; i++) {
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    answer = {
      run_id: answer.run_id,
      ...(await dataProvider.leadSearch<SearchAnswer>({
        action: "run_status",
        run_id: answer.run_id,
      })),
    };
  }
  if (!answer.results) throw new Error(stillRunning);
  return answer;
};

/** An ideal customer profile as plain text, for the AI */
export const profileText = (profile: IdealCustomer) =>
  [
    profile.offer && `We sell: ${profile.offer}`,
    profile.customer_problem && `Their problem: ${profile.customer_problem}`,
    profile.target_role && `Contact: ${profile.target_role}`,
    profile.target_geography && `Where: ${profile.target_geography}`,
    profile.target_company_size && `Size: ${profile.target_company_size}`,
    ...(profile.positive_criteria ?? []),
  ]
    .filter(Boolean)
    .join("\n");

/** The lead agent's 0-100 rating as the company's fit score (0-5) with its reason, saved on "Add as leads" */
export const agentFit = (fit: number, reason: string, segment: string) => {
  const fit_score = Math.round(Math.min(100, Math.max(0, fit)) / 20);
  return {
    fit_score,
    fit_match: fit_score >= 4 ? 3 : fit_score >= 2 ? 2 : 1,
    fit_reasons: {
      [segment]: { score: fit_score, reasoning: reason, kind: "good" },
    },
    fit_profile: "Lead agent",
    fit_scored_at: new Date().toISOString(),
  };
};
